import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { GridItem, GridSection } from 'src/components/CustomModal';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { useHealthcareServices, useOrganizations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { HealthcareService } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/HealthcareService';
import { WrappedOrganization, OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { WrappedHealthcareService, HealthcareServiceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { ContactPoint, Identifier, Organization, ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { LoadingButton } from '@mui/lab';
import { createOrganization } from 'src/careflow/ph-core/admin/organization/service/organizations';
import { fhirClient } from 'src/App';
import Iconify from 'src/components/Iconify';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import DialogCloseButton from 'src/components/DialogCloseButton';

type FormValues = {
  serviceName: string;
  organization: any; // Using any to avoid circular type references
  serviceType?: ValueSetComposeIncludeConcept | null;
  category?: ValueSetComposeIncludeConcept | null;
  comment?: string;
  email?: string;
  phone?: string;
  taxId?: string;
  npi?: string;
};

type NewOrganizationFormValues = {
  name: string;
};

type OrganizationOption = {
  label: string;
  value: WrappedOrganization | 'new';
};

interface NewServiceModalProps {
  open: boolean;
  onClose: () => void;
  onServiceCreated: (service: {
    label: string;
    value: {
      healthcareService: WrappedHealthcareService;
      organization: WrappedOrganization;
    };
  }) => void;
  initialServiceName?: string;
}

const FHIR_META_TAG_SYSTEM = 'https://terminology.patientholistics.app/fhir/CodeSystem/ph-meta-tags';

export default function NewServiceModal({
  open,
  onClose,
  onServiceCreated,
  initialServiceName = '',
}: NewServiceModalProps) {
  const { i18n } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const { componentsData, configurations } = useTenantConfigData();
  const authUser = useAuth().getCurrentUser();
  
  // Get tenant config for conditional requirements
  const referralConfig = componentsData?.referral ?? {} as any;
  const requireContact = referralConfig.requireServiceContact ?? false;
  const requireIdentifier = referralConfig.requireServiceIdentifier ?? false;
  
  const [isCreating, setIsCreating] = useState(false);
  const [isNewOrgModalOpen, setIsNewOrgModalOpen] = useState(false);
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<WrappedOrganization | null>(null);
  
  const [, { create: createHealthcareService }] = useHealthcareServices({ autofetch: false });
  const [organizations, { isFetching: isOrganizationsLoading, refresh: refreshOrganizations }] = useOrganizations({
    pagination: {
      pageSize: 1000,
    },
    map: OrganizationWrapper,
    autofetch: open, // Only fetch organizations when modal is open
  });

  // Fetch Service Type and Category ValueSets - only when modal is open
  const {
    valueSets: [serviceTypeVS, serviceRequestCategoryVS],
    isLoading: isValueSetsLoading,
  } = useValueSetsByIdentifiers(['ph-referral-services', 'ph-service-request-category'], {
    enabled: open, // Only fetch when modal is open
  });

  const isServiceTypeLoading = isValueSetsLoading;
  const isCategoryLoading = isValueSetsLoading;

  // Map Service Type ValueSet to options
  const serviceTypeOptions = useMemo(() => {
    return serviceTypeVS?.asListAll?.() ?? [];
  }, [serviceTypeVS]);

  // Map Category ValueSet to options
  const categoryOptions = useMemo(() => {
    return serviceRequestCategoryVS?.asListAll?.() ?? [];
  }, [serviceRequestCategoryVS]);

  // Build validation schema based on tenant config
  const validationSchema = useMemo(() => {
    const baseSchema: any = {
      serviceName: Yup.string().required(i18n('errorMessages.fieldIsRequired', 'crs')),
      organization: Yup.mixed().nullable().required(i18n('errorMessages.fieldIsRequired', 'crs')),
    };

    // Conditional validation for contact
    if (requireContact) {
      baseSchema.email = Yup.string()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test('contact-required', 'At least one contact (email or phone) is required', function(value) {
          const { phone } = this.parent;
          const hasEmail = value && value.trim().length > 0;
          const hasPhone = phone && phone.trim().length > 0;
          return !!(hasEmail || hasPhone);
        })
        .test('email-format', 'Email must be a valid email address', function(value) {
          if (!value || value.trim().length === 0) return true; // Skip if empty (phone might be provided)
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        });
      
      baseSchema.phone = Yup.string()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test('contact-required', 'At least one contact (email or phone) is required', function(value) {
          const { email } = this.parent;
          const hasEmail = email && email.trim().length > 0;
          const hasPhone = value && value.trim().length > 0;
          return !!(hasEmail || hasPhone);
        })
        .test('phone-format', 'Please enter a valid phone number', function(value) {
          if (!value || value.trim().length === 0) return true; // Skip if empty (email might be provided)
          return /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(value);
        });
    } else {
      baseSchema.email = Yup.string()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test('email-format', 'Email must be a valid email address', function(value) {
          if (!value || value.trim().length === 0) return true;
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        });
      baseSchema.phone = Yup.string()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test('phone-format', 'Please enter a valid phone number', function(value) {
          if (!value || value.trim().length === 0) return true;
          return /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(value);
        });
    }

    // Conditional validation for identifiers
    if (requireIdentifier) {
      baseSchema.taxId = Yup.string()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test('identifier-required', 'At least one identifier (Tax ID or NPI) is required', function(value) {
          const { npi } = this.parent;
          const hasTaxId = value && value.trim().length > 0;
          const hasNpi = npi && npi.trim().length > 0;
          return !!(hasTaxId || hasNpi);
        });
      
      baseSchema.npi = Yup.string()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test('identifier-required', 'At least one identifier (Tax ID or NPI) is required', function(value) {
          const { taxId } = this.parent;
          const hasTaxId = taxId && taxId.trim().length > 0;
          const hasNpi = value && value.trim().length > 0;
          return !!(hasTaxId || hasNpi);
        });
    } else {
      baseSchema.taxId = Yup.string().nullable().transform((curr, orig) => (orig === '' ? null : curr));
      baseSchema.npi = Yup.string().nullable().transform((curr, orig) => (orig === '' ? null : curr));
    }

    return Yup.object().shape(baseSchema);
  }, [requireContact, requireIdentifier, i18n]);

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      serviceName: initialServiceName,
      organization: null,
      serviceType: null,
      category: null,
      comment: '',
      email: '',
      phone: '',
      taxId: '',
      npi: '',
    },
  });

  const {
    handleSubmit,
    reset,
    setValue: setValueOriginal,
    watch: watchOriginal,
    formState: { errors },
  } = methods;

  // Cast setValue and watch to avoid TypeScript circular reference issues with FHIR types
  const setValue = setValueOriginal as any;
  const watch = watchOriginal as any;

  const organizationOptions = useMemo<OrganizationOption[]>(() => {
    return (organizations as WrappedOrganization[])?.map((org) => ({
      label: org.name || '',
      value: org,
    })) ?? [];
  }, [organizations]);

  // New Organization Form
  const newOrgMethods = useForm<NewOrganizationFormValues>({
    resolver: yupResolver(Yup.object().shape({
      name: Yup.string().required(i18n('errorMessages.fieldIsRequired', 'crs')),
    })),
    defaultValues: {
      name: '',
    },
  });

  const handleCreateNewOrganization = async (data: NewOrganizationFormValues) => {
    setIsCreatingOrg(true);
    try {
      const newOrg = await createOrganization({
        name: data.name,
        active: true,
      } as any);
      
      // Fetch the created organization to get full details
      if (newOrg?.fhirId) {
        // Fetch the FHIR organization resource directly
        const fhirOrg = await fhirClient.findById('Organization',newOrg.fhirId);
        if (fhirOrg) {
          const wrappedOrg = OrganizationWrapper(fhirOrg as Organization);
          // Refresh organizations list
          await refreshOrganizations();
          // Select the newly created organization
          setSelectedOrganization(wrappedOrg);
          // Use setTimeout to avoid TypeScript circular reference issues
          setTimeout(() => {
            (setValue as any)('organization', wrappedOrg, { shouldValidate: false });
          }, 0);
          setIsNewOrgModalOpen(false);
          newOrgMethods.reset();
          enqueueSnackbar('Organization created successfully', { variant: 'success' });
        } else {
          throw new Error('Failed to retrieve created organization');
        }
      } else {
        throw new Error('Failed to create organization');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      enqueueSnackbar('Failed to create organization', { variant: 'error' });
    } finally {
      setIsCreatingOrg(false);
    }
  };

  const handleCreateService = async (data: FormValues) => {
    setIsCreating(true);
    
    try {
      const organizationToUse = data.organization;

      if (!organizationToUse) {
        enqueueSnackbar('Organization is required', { variant: 'error' });
        setIsCreating(false);
        return;
      }

      // Build telecom array
      const telecom: ContactPoint[] = [];
      if (data.email) {
        telecom.push({
          system: 'email',
          value: data.email,
        });
      }
      if (data.phone) {
        // Remove formatting for storage
        const phoneDigits = data.phone.replace(/\D/g, '');
        telecom.push({
          system: 'phone',
          value: phoneDigits,
        });
      }

      // Build identifier array
      const identifier: Identifier[] = [];
      if (data.taxId) {
        identifier.push({
          system: 'http://hl7.org/fhir/sid/us-ein',
          value: data.taxId,
        });
      }
      if (data.npi) {
        identifier.push({
          system: 'http://hl7.org/fhir/sid/us-npi',
          value: data.npi,
        });
      }

      // Create HealthcareService resource
      const now = new Date().toISOString();
      
      // Build type array (Service Type) - CodeableConcept[]
      const type = data.serviceType ? [{
        coding: [{
          system: data.serviceType.system,
          code: data.serviceType.code,
          display: data.serviceType.display,
        }],
        text: data.serviceType.display,
      }] : undefined;

      // Build category array - CodeableConcept[]
      const category = data.category ? [{
        coding: [{
          system: data.category.system,
          code: data.category.code,
          display: data.category.display,
        }],
        text: data.category.display,
      }] : undefined;

      const healthcareServicePayload: HealthcareService = {
        resourceType: 'HealthcareService',
        active: true,
        name: data.serviceName,
        providedBy: {
          reference: `Organization/${organizationToUse.id}`,
          display: organizationToUse.name,
        },
        ...(type && { type }),
        ...(category && { category }),
        ...(data.comment && { comment: data.comment }),
        ...(telecom.length > 0 && { telecom }),
        ...(identifier.length > 0 && { identifier }),
        meta: {
          tag: [
            {
              system: FHIR_META_TAG_SYSTEM,
              code: 'ph-manually-added',
              display: 'true',
            },
            {
              system: FHIR_META_TAG_SYSTEM,
              code: 'ph-created-by',
              display: authUser?.username || 'unknown',
            },
            {
              system: FHIR_META_TAG_SYSTEM,
              code: 'ph-created-on',
              display: now,
            },
          ],
        },
      };

      const [createdService] = await createHealthcareService(healthcareServicePayload);
      
      if (!createdService) {
        throw new Error('Failed to create healthcare service');
      }

      // Wrap the created service
      const wrappedService = HealthcareServiceWrapper(createdService);
      
      // Callback with the new service
      onServiceCreated({
        label: `${data.serviceName} | ${organizationToUse.name}`,
        value: {
          healthcareService: wrappedService,
          organization: organizationToUse,
        },
      });

      enqueueSnackbar('Service created successfully', { variant: 'success' });
      handleClose();
    } catch (error) {
      console.error('Error creating service:', error);
      enqueueSnackbar('Failed to create service', { variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedOrganization(null);
    setIsNewOrgModalOpen(false);
    newOrgMethods.reset();
    onClose();
  };

  const handleOrganizationChange = (_: any, value: OrganizationOption | null) => {
    if (!value) {
      setSelectedOrganization(null);
      (setValue as any)('organization', null, { shouldValidate: false });
      return;
    }
    
    // Don't handle 'new' here - it's handled in renderOption onClick
    if (value.value === 'new') {
      return;
    }
    
    const orgValue = value.value as WrappedOrganization;
    setSelectedOrganization(orgValue);
    (setValue as any)('organization', orgValue, { shouldValidate: false });
  };

  // Format phone number input
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('phone', formatted, { shouldValidate: true });
  };

  if (!open) return null;

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 3,
          bgcolor: 'background.paper',
          height: 'fit-content',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            {i18n('newService', 'crs') || 'New Service'}
          </Typography>
          <Button
            onClick={handleClose}
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            <Iconify icon="eva:close-fill" width={20} height={20} />
          </Button>
        </Box>

        <FormProvider methods={methods} onSubmit={handleSubmit(handleCreateService as any)}>
          <GridSection>
            <GridItem xs={12}>
              <RHFTextField
                name="serviceName"
                label="Service Name *"
                variant="outlined"
                fullWidth
              />
            </GridItem>
          </GridSection>

          <GridSection>
            <GridItem xs={5.9}>
              <Autocomplete
                fullWidth
                disabled={!serviceTypeVS}
                options={serviceTypeOptions}
                value={watch('serviceType')}
                onChange={(_: any, value: any) => {
                  setValue('serviceType', value, { shouldValidate: false });
                }}
                getOptionLabel={(option: ValueSetComposeIncludeConcept) => option.display ?? ''}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                loading={isServiceTypeLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Service Type"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isServiceTypeLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </GridItem>
            <GridItem xs={5.9}>
              <Autocomplete
                fullWidth
                disabled={!serviceRequestCategoryVS}
                options={categoryOptions}
                value={watch('category')}
                onChange={(_: any, value: any) => {
                  setValue('category', value, { shouldValidate: false });
                }}
                getOptionLabel={(option: ValueSetComposeIncludeConcept) => option.display ?? ''}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                loading={isCategoryLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isCategoryLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </GridItem>
          </GridSection>

        <GridSection>
          <GridItem xs={12}>
            <Autocomplete<OrganizationOption>
              fullWidth
              options={[
                ...organizationOptions,
                { label: '(+) Add New Organization', value: 'new' as const },
              ]}
              value={selectedOrganization ? { label: selectedOrganization.name || '', value: selectedOrganization } : null}
              onChange={handleOrganizationChange}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => {
                if (option.value === 'new' || value.value === 'new') {
                  return false; // Never match 'new' as a selected value
                }
                return (option.value as WrappedOrganization)?.id === (value.value as WrappedOrganization)?.id;
              }}
              filterOptions={(options, state) => {
                // Filter out 'new' option from being selectable via keyboard/typing
                const filtered = options.filter(opt => opt.value !== 'new');
                // Always add 'new' option at the end
                return [...filtered, { label: '(+) Add New Organization', value: 'new' as const }];
              }}
              loading={isOrganizationsLoading}
              renderOption={(props, option) => {
                const optionAny = option as any;
                if (optionAny.value === 'new') {
                  return (
                    <li
                      {...props}
                      key="add-new-org"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsNewOrgModalOpen(true);
                        // Close the autocomplete dropdown
                        const input = e.currentTarget.closest('.MuiAutocomplete-root')?.querySelector('input');
                        if (input) {
                          input.blur();
                        }
                      }}
                      style={{ cursor: 'pointer', fontWeight: 500 }}
                    >
                      <span style={{ marginRight: 8 }}>+</span>
                      Add New Organization
                    </li>
                  );
                }
                return <li {...props}>{option.label}</li>;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organization *"
                  variant="outlined"
                  error={!!errors.organization}
                  helperText={errors.organization && typeof errors.organization === 'object' && 'message' in errors.organization ? String(errors.organization.message) : ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isOrganizationsLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </GridItem>
        </GridSection>


        <GridSection>
          <GridItem xs={5.9}>
            <RHFTextField
              name="email"
              label={`Email${requireContact ? ' *' : ''}`}
              variant="outlined"
              fullWidth
              type="email"
            />
          </GridItem>
          <GridItem xs={5.9}>
            <TextField
              fullWidth
              label={`Phone${requireContact ? ' *' : ''}`}
              variant="outlined"
              value={watch('phone') || ''}
              onChange={handlePhoneChange}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              placeholder="(123) 456-7890"
            />
          </GridItem>
        </GridSection>

        <GridSection>
          <GridItem xs={5.9}>
            <RHFTextField
              name="taxId"
              label={`Tax Id${requireIdentifier ? ' *' : ''}`}
              variant="outlined"
              fullWidth
            />
          </GridItem>
          <GridItem xs={5.9}>
            <RHFTextField
              name="npi"
              label={`Npi${requireIdentifier ? ' *' : ''}`}
              variant="outlined"
              fullWidth
            />
          </GridItem>
        </GridSection>

        <GridSection>
          <GridItem xs={12}>
            <RHFTextField
              name="comment"
              label="Comment"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
            />
          </GridItem>
        </GridSection>

        {requireContact && (
          <GridSection>
            <GridItem xs={12}>
              <Typography variant="caption" color="text.secondary">
                * At least one contact (email or phone) is required
              </Typography>
            </GridItem>
          </GridSection>
        )}

        {requireIdentifier && (
          <GridSection>
            <GridItem xs={12}>
              <Typography variant="caption" color="text.secondary">
                * At least one identifier (Tax ID or NPI) is required
              </Typography>
            </GridItem>
          </GridSection>
        )}

        <GridSection>
          <GridItem xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isCreating}
              >
                Close
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isCreating}
                onClick={handleSubmit(handleCreateService as any)}
              >
                Add
              </LoadingButton>
            </Box>
          </GridItem>
        </GridSection>
        </FormProvider>
      </Box>

      {/* New Organization Dialog */}
      <Dialog
        open={isNewOrgModalOpen}
        onClose={() => {
          setIsNewOrgModalOpen(false);
          newOrgMethods.reset();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton
          onClose={() => {
            setIsNewOrgModalOpen(false);
            newOrgMethods.reset();
          }}
          hasUnsavedChanges={newOrgMethods.formState.isDirty}
          disabled={isCreatingOrg}
        />
        <FormProvider methods={newOrgMethods} onSubmit={newOrgMethods.handleSubmit(handleCreateNewOrganization)}>
          <DialogTitle>
            {i18n('newOrganization', 'crs') || 'Add New Organization'}
          </DialogTitle>
          <DialogContent>
            <RHFTextField
              name="name"
              label={`${i18n('newOrganization.name', 'crs') || 'Organization Name'} *`}
              variant="outlined"
              fullWidth
              autoFocus
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setIsNewOrgModalOpen(false);
                newOrgMethods.reset();
              }}
              disabled={isCreatingOrg}
            >
              {i18n('cancel', 'crs') || 'Cancel'}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isCreatingOrg}
              onClick={newOrgMethods.handleSubmit(handleCreateNewOrganization)}
            >
              {i18n('add', 'crs') || 'Add'}
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
}
